import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LayoutDashboard, CalendarCheck, Users, Syringe, FileText, Settings, LogOut, Menu, X, Phone, Bell, Radio, TrendingUp, ShieldCheck, BellRing, BellOff, Clock4, PauseCircle } from 'lucide-react';
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import config from '../../config';
import { brandLogo, useSiteContent } from '../../lib/siteContent';

// Short, pleasant chime — data-URI (no external file, no network)
// Two-tone bell (A5 → E6) via WebAudio, ~0.4s, 30% volume
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tones = [
      { f: 880, t: now,       d: 0.22 },  // A5
      { f: 1318.5, t: now+0.13, d: 0.30 }, // E6
    ];
    tones.forEach(({ f, t, d }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.25, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + d);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + d + 0.02);
    });
    setTimeout(() => ctx.close().catch(() => {}), 800);
  } catch (err) { if (process.env.NODE_ENV === 'development') console.warn(err); }
}

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/live-app-ops', label: 'App Operations', icon: Radio },
  { path: '/admin/bookings', label: 'Bookings', icon: CalendarCheck },
  { path: '/admin/callbacks', label: 'Callbacks', icon: Phone },
  { path: '/admin/customers', label: 'Customers & families', icon: Users },
  { path: '/admin/nurses', label: 'Staff', icon: Syringe },
  { path: '/admin/nurse-attendance', label: 'Nurse Attendance', icon: Clock4 },
  { path: '/admin/records', label: 'Records & consent', icon: FileText },
  { path: '/admin/cms', label: 'CMS & Catalogue', icon: FileText },
  { path: '/admin/invoices', label: 'Invoices & Revenue', icon: TrendingUp },
  { path: '/admin/analytics', label: 'Analytics', icon: TrendingUp, role: 'super_admin' },
  { path: '/admin/site-status', label: 'Site status', icon: PauseCircle, role: 'super_admin' },
  { path: '/admin/security', label: 'Security', icon: ShieldCheck, role: 'super_admin' },
  { path: '/admin/invoice-settings', label: 'Invoice Settings', icon: Settings },
];

export default function AdminLayout() {
  const { user, logout, getAuthHeaders } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingCallbacks, setPendingCallbacks] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Browser-push state — remembers last seen callback count across polls
  const lastCallbackCountRef = useRef(null);
  const [pushPerm, setPushPerm] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const [bellPulse, setBellPulse] = useState(false);

  const fireDesktopAlert = useCallback((newCount, topItem) => {
    // 1. Visual pulse on bell
    setBellPulse(true);
    setTimeout(() => setBellPulse(false), 4000);

    // 2. Audible chime
    playChime();

    // 3. Browser OS notification (if granted)
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        const title = newCount === 1
          ? 'BookMyJab — New lead'
          : `BookMyJab — ${newCount} new leads`;
        const body = topItem
          ? `${topItem.title}\n${topItem.message || topItem.body || ''}`.trim()
          : 'A new callback request just came in.';
        const n = new Notification(title, {
          body,
          icon: '/icon-192.png',
          badge: '/favicon-32x32.png',
          tag: 'bmj-new-lead',
          renotify: true,
        });
        n.onclick = () => {
          window.focus();
          navigate('/admin/callbacks');
          n.close();
        };
        setTimeout(() => n.close(), 8000);
      } catch (err) { if (process.env.NODE_ENV === 'development') console.warn(err); }
    }

    // 4. Update document title briefly
    if (typeof document !== 'undefined') {
      const original = document.title;
      document.title = `(${newCount}) New lead — ${original}`;
      setTimeout(() => { document.title = original; }, 6000);
    }
  }, [navigate]);

  const fetchCounts = useCallback(async () => {
    const h = { headers: getAuthHeaders() };
    try {
      const [dashRes, notifRes] = await Promise.all([
        axios.get(`${config.API}/admin/dashboard`, h),
        axios.get(`${config.API}/admin/notifications`, h),
      ]);
      const pending = dashRes.data.pending_callbacks || 0;
      const notifs = notifRes.data.notifications || [];
      setPendingCallbacks(pending);
      setUnreadNotifs(notifRes.data.unread_count || 0);
      setNotifications(notifs);

      // Detect new leads: compare pending count to last seen
      if (lastCallbackCountRef.current === null) {
        lastCallbackCountRef.current = pending;
      } else if (pending > lastCallbackCountRef.current) {
        const delta = pending - lastCallbackCountRef.current;
        const topCallback = notifs.find(n => n.type === 'callback_request');
        fireDesktopAlert(delta, topCallback);
        lastCallbackCountRef.current = pending;
      } else if (pending < lastCallbackCountRef.current) {
        // admin marked some as contacted — just sync
        lastCallbackCountRef.current = pending;
      }
    } catch (err) { if (process.env.NODE_ENV === 'development') console.warn(err); }
  }, [getAuthHeaders, fireDesktopAlert]);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000); // Poll every 30s for new leads
    return () => clearInterval(interval);
  }, [fetchCounts]);

  // SEO: noindex + nofollow admin surface (defense-in-depth; robots.txt also blocks it)
  useEffect(() => {
    const existing = document.querySelector('meta[name="robots"]');
    const prev = existing ? existing.getAttribute('content') : null;
    const tag = existing || document.createElement('meta');
    tag.setAttribute('name', 'robots');
    tag.setAttribute('content', 'noindex, nofollow');
    if (!existing) document.head.appendChild(tag);
    return () => {
      if (prev !== null) {
        tag.setAttribute('content', prev);
      } else if (tag.parentNode) {
        tag.parentNode.removeChild(tag);
      }
    };
  }, []);

  const requestPushPerm = async () => {
    if (typeof Notification === 'undefined') return;
    try {
      const res = await Notification.requestPermission();
      setPushPerm(res);
      if (res === 'granted') {
        // Fire a friendly confirmation
        new Notification('BookMyJab alerts enabled', {
          body: "You'll be notified here the moment a new lead arrives.",
          icon: '/icon-192.png',
        });
      }
    } catch (err) { if (process.env.NODE_ENV === 'development') console.warn(err); }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${config.API}/admin/notifications/mark-read`, {}, { headers: getAuthHeaders() });
      setUnreadNotifs(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) { if (process.env.NODE_ENV === 'development') console.warn(err); }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin-login');
  };

  const isActive = (path) => location.pathname === path || (path === '/admin/dashboard' && location.pathname === '/admin');
  const site = useSiteContent();
  const logoSrc = brandLogo(site);

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-[#f8f9fa] flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#311b52] text-white flex flex-col transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="admin-sidebar">
        <div className="flex flex-col gap-3 px-6 py-4 border-b border-white/10">
          <Link
            to="/"
            className="flex flex-col items-start justify-center shrink-0"
            style={{ minWidth: '150px' }}
          >
            <img
              src={logoSrc}
              alt="BookMyJab"
              style={{
                width: '140px',
                height: 'auto',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)'
              }}
            />
            <span
              className="text-[8px] md:text-[9px] italic font-medium text-white/70 tracking-[0.12em] uppercase leading-tight mt-0.5"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Uncompromising Care. Seamlessly Yours.
            </span>
          </Link>
          <span className="text-[10px] font-medium text-white/60 bg-white/10 rounded px-2 py-0.5 self-start">Admin</span>
        </div>

        <nav className="flex-1 overflow-y-auto mt-4 px-3 space-y-1 pb-4" data-testid="admin-nav">
          {navItems.filter(i => !i.role || i.role === user?.role).map((item) => (
            <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.path) ? 'bg-white/15 text-white' : 'text-white/60 hover:bg-white/10 hover:text-white'
              }`}>
              <div className="flex items-center gap-3">
                <item.icon className="w-4 h-4" />
                {item.label}
              </div>
              {item.label === 'Callbacks' && pendingCallbacks > 0 && (
                <span className="bg-[#f37721] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{pendingCallbacks}</span>
              )}
            </Link>
          ))}
        </nav>

        <div className="shrink-0 p-4 border-t border-white/10 mt-auto bg-[#311b52]">
          <div className="text-xs text-white/40 mb-2 px-3">{user?.email}</div>
          <button onClick={handleLogout} data-testid="admin-logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/60 hover:bg-white/10 hover:text-white w-full transition-colors">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 lg:ml-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-white border-b border-[#673c96]/10 h-14 flex items-center px-4 lg:px-8 gap-4">
          <button className="lg:hidden p-1.5 text-[#311b52]" onClick={() => setSidebarOpen(true)} data-testid="sidebar-toggle">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>
              {navItems.find(n => isActive(n.path))?.label || 'Dashboard'}
            </h2>
          </div>

          {/* Push notification toggle */}
          <button
            onClick={requestPushPerm}
            data-testid="push-toggle"
            title={
              pushPerm === 'granted' ? 'Desktop alerts enabled'
              : pushPerm === 'denied' ? 'Desktop alerts blocked — enable in browser settings'
              : pushPerm === 'unsupported' ? 'Desktop alerts not supported in this browser'
              : 'Enable desktop alerts for new leads'
            }
            disabled={pushPerm === 'denied' || pushPerm === 'unsupported'}
            className={`p-2 rounded-lg transition-colors ${
              pushPerm === 'granted'
                ? 'text-[#22c55e] bg-[#22c55e]/10 hover:bg-[#22c55e]/15'
                : pushPerm === 'denied' || pushPerm === 'unsupported'
                  ? 'text-[#673c96]/30 cursor-not-allowed'
                  : 'text-[#f37721] bg-[#f37721]/10 hover:bg-[#f37721]/15'
            }`}
          >
            {pushPerm === 'granted' ? <BellRing className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
          </button>

          {/* Notification bell */}
          <div className="relative">
            <button onClick={() => { setShowNotifs(!showNotifs); if (!showNotifs && unreadNotifs > 0) markAllRead(); }}
              data-testid="notification-bell" className={`relative p-2 text-[#673c96] hover:text-[#f37721] transition-colors ${bellPulse ? 'animate-pulse' : ''}`}>
              <Bell className={`w-5 h-5 ${bellPulse ? 'text-[#f37721]' : ''}`} />
              {unreadNotifs > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#f37721] text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {unreadNotifs}
                </span>
              )}
            </button>

            {/* Notification dropdown */}
            {showNotifs && (
              <div className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-2xl border border-[#673c96]/10 z-50 max-h-96 overflow-y-auto" data-testid="notification-dropdown">
                <div className="px-4 py-3 border-b border-[#673c96]/10">
                  <span className="text-sm font-semibold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>Notifications</span>
                </div>
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-[#673c96]/50">No notifications</div>
                ) : (
                  notifications.slice(0, 10).map(n => (
                    <div key={n.id} className={`px-4 py-3 border-b border-[#673c96]/5 ${!n.read ? 'bg-[#f37721]/[0.03]' : ''}`}>
                      <div className="flex items-start gap-2">
                        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${!n.read ? 'bg-[#f37721]' : 'bg-[#673c96]/20'}`} />
                        <div>
                          <p className="text-xs font-semibold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>{n.title}</p>
                          <p className="text-[11px] text-[#673c96]/70 mt-0.5" style={{ fontFamily: 'Montserrat' }}>{n.message || n.body}</p>
                          <p className="text-[10px] text-[#673c96]/40 mt-1">
                            {n.created_at ? new Date(n.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {notifications.length > 0 && (
                  <Link to="/admin/callbacks" onClick={() => setShowNotifs(false)}
                    className="block px-4 py-2.5 text-center text-xs font-semibold text-[#f37721] hover:bg-[#f37721]/5 transition-colors">
                    View All Callbacks
                  </Link>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#673c96] flex items-center justify-center text-white text-xs font-bold">
              {user?.full_name?.[0] || 'A'}
            </div>
            <span className="hidden sm:block text-xs font-medium text-[#3e195e]">{user?.full_name || 'Admin'}</span>
          </div>
        </header>

        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Click outside to close notification dropdown */}
      {showNotifs && <div className="fixed inset-0 z-10" onClick={() => setShowNotifs(false)} />}
    </div>
  );
}
